import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import SpinnerIcon from "../../components/global/SpinnerIcon";
import { resumeSubscription } from "../../store/actions/subscribeAction";

/*
    MY SUBSCRIPTION STATUS — driven by GET /me/entitlement (backend#103).

    This component is the ONLY route to /main/cancel-membership anywhere in the
    app, which is why what it renders, and when, is a bigger deal than it
    looks.

    What it used to do, and why each part was wrong:

      - `if (user?.api_plan === "test") return;` suppressed the whole panel for
        free customers, so they could never cancel. It also returned undefined
        rather than null.
      - It read `cancel_at` from a live Stripe subscription, then printed a
        date from `user.api_active_until` — two sources, and the second is
        NULL until a customer's second payment, so the panel could render
        "access until Invalid date".
      - The cancel link lived in a single else-branch, so a customer with a
        scheduled plan change lost it entirely.
      - Its parent gated it on `user.stripe_customer_id`, which is blank for
        every free and comped customer and stale for anyone who paid since
        their last sign-in — the defect Dave reported on 2026-09-18.

    Now there is one input and one rule: render what `status` says, and show
    the cancel link exactly when the server says `canCancel`. The server sets
    that true only where a cancel would actually succeed, so the link can no
    longer lead to a page that fails at the customer.
*/

const formatDate = (iso) =>
  iso && moment(iso).isValid() ? moment(iso).format("MMMM DD, YYYY") : null;

const CancelLink = () => (
  <div className="pt-3">
    <Link to="/main/cancel-membership" className="link-to fw--500">
      Cancel your subscription
    </Link>
  </div>
);

/*
    One label for every branch. This used to be an <h3> in four places and an
    <h5> in a fifth, which put three different heading levels on one panel
    depending on what the customer's subscription happened to be doing.
*/
const StatusLabel = () => (
  <span className="account__fact-label">Subscription Status:</span>
);

const Reactivate = ({ children }) => (
  <>
    <p className="py-2 mb-0">{children}</p>
    <Link to="/api/plans" className="link-to">
      Reactivate your Subscription
    </Link>
  </>
);

/*
    A cancelled plan is still paid for until `activeUntil`, so the customer can
    take the cancel back (static#104). This used to be "Reactivate your
    Subscription", a link to API Plans — where their plan was disabled and
    checkout refused it, so there was no way back until the period ran out.

    `onResumed` re-fetches the entitlement, so the panel flips to "Active …
    renew automatically on …" from what the server now says.

    Only "cancelling" gets this. "paused" and "ended" really have ended, and
    starting a plan from API Plans is the right answer for them.
*/
const ResumeButton = ({ onResumed }) => {
  const dispatch = useDispatch();
  const [resuming, setResuming] = useState(false);

  const resume = async () => {
    setResuming(true);
    const resumed = await dispatch(resumeSubscription());
    setResuming(false);
    if (resumed) onResumed();
  };

  return (
    <button
      type="button"
      className="gb-btn gb-btn--primary"
      disabled={resuming}
      onClick={resume}
    >
      {resuming ? (
        <>
          <SpinnerIcon label="Resuming your subscription" /> Resuming
        </>
      ) : (
        "Resume my subscription"
      )}
    </button>
  );
};

const SubscriptionStatus = ({ entitlement, onResumed = () => {} }) => {
  const status = entitlement?.status;
  if (!status || status === "none") return null;

  const { canCancel, activeUntil, billing } = entitlement;
  const endsOn = formatDate(activeUntil);
  const renewsOn = formatDate(billing?.renewsOn);
  const nextPlan = billing?.nextPlan;

  if (status === "cancelling") {
    return (
      <div className="mt-4">
        <p className="account__fact">
          <StatusLabel /> Cancelled.
        </p>
        <p className="py-2 mb-0">
          Your subscription is cancelled.
          {endsOn ? ` You have access until ${endsOn}` : ""}
        </p>
        <ResumeButton onResumed={onResumed} />
      </div>
    );
  }

  if (status === "paused") {
    return (
      <div className="mt-4">
        <p className="account__fact">
          <StatusLabel /> Paused.
        </p>
        <Reactivate>
          Your API access is paused. Start a plan again to switch it back on.
        </Reactivate>
      </div>
    );
  }

  if (status === "ended") {
    return (
      <div className="mt-4">
        <p className="account__fact">
          <StatusLabel /> Ended.
        </p>
        <Reactivate>Your plan has ended.</Reactivate>
      </div>
    );
  }

  if (status === "unsupported") {
    // A real, often paying customer on a plan this system has no rule for.
    // Nothing they can do in the UI will fix it, so point at a human rather
    // than at the Plans page.
    return (
      <div className="mt-4">
        <p className="account__fact">
          <StatusLabel /> Needs attention.
        </p>
        <p className="account__fact mb-0">
          We need to sort your plan out by hand. Please contact{" "}
          <a href="mailto:apisupport@golfbert.com" className="link-to">
            apisupport@golfbert.com
          </a>
          .
        </p>
      </div>
    );
  }

  // active
  return (
    <div className="mt-4">
      <p className="account__fact mb-0">
        <StatusLabel /> Active.
        {nextPlan ? (
          <>
            {" "}
            Your subscription will remain active until {renewsOn}. After that,
            your plan will automatically switch to {nextPlan}.
          </>
        ) : renewsOn ? (
          <>
            {" "}
            Your subscription is scheduled to renew automatically on {renewsOn}.
          </>
        ) : null}
      </p>
      {/*
          The cancel link renders in EVERY active state now — including a
          scheduled plan change, and including the free plan, which never had
          a way out at all. #84 tried to fix this in the component and was
          correctly reverted (#85), because the parent's gate made the
          component unreachable for the customers it was written for. The gate
          is gone; this is the other half.
      */}
      {canCancel && <CancelLink />}
    </div>
  );
};

export default SubscriptionStatus;
